import { ChangeDetectorRef, Component, OnInit, Renderer2, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RegionImpl, RegionDetailModel, watchActiveRegion, watchEnabledRegions } from 'shell-browser/region.js';
import { NgShellRxjsWrapperService } from './shell/ng-shell-wrapper.service';
import { CustomerNotificationService } from './features/main-page/customer-notification.service';
import { watchIsLoggedIn } from 'shell-browser/auth.js';
import { filter, map, Observable, Subscription, distinctUntilChanged } from 'rxjs';
import { LinkService } from "./core/services/link.service";

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})

export class AppComponent implements OnInit {
  readonly ngShellService = inject(NgShellRxjsWrapperService);
  readonly translocoService = inject(TranslocoService);
  readonly linkService = inject(LinkService);
  router = inject(Router);
  changeDetect = inject(ChangeDetectorRef);
  customerservice = inject(CustomerNotificationService);
  routerSubscription: Subscription;
  selectedValue: any;
  isLoggenIn: boolean = false;
  selectOptList:any = [{
    label: 'My notifications',
    value: 'My notifications',
    title: 'My notifications'
  }];
  localId:string = '';
  localIdBool:boolean = false;

  ngOnInit(): void {
    this.getShellRouters();
    this.setShellLanguages();

    this.ngShellService.toObservable(watchIsLoggedIn)
    .pipe(
        distinctUntilChanged(), // Only emit when value changes
        untilDestroyed(this)
      )
    .subscribe(
      isLoggedIn => {
        this.isLoggenIn = isLoggedIn
        this.onLoginAction();
      }
    );
  }
  
  private onLoginAction(){

    if (this.isLoggenIn) {
      this.makeAccountCall();
      this.getLocalId();
      this.navigationToggle();
      this.customerservice.accountSelector$
        .pipe(untilDestroyed(this))
        .subscribe(res => {
          this.selectedValue = res
        });
    }
  }

  private getShellRouters(): void {
    this.ngShellService
      .toObservable(watchActiveRegion)
      .pipe(untilDestroyed(this))
      .subscribe((region: RegionImpl) => {
        if (region.country && region.ietf) {
          this.linkService.setRegionalizedRoute(`${region.country}/${region.ietf.toLowerCase()}`);
          this.refreshRouteAfterLanguageChange();
          this.translocoService.setActiveLang(region.ietf);
        }
      });
  }

  private refreshRouteAfterLanguageChange(): void {
    const currentRouteWithoutRegionalization = this.router?.url.split('/').pop() ?? 'main';
    const newRoute = this.linkService.getRouterLink(currentRouteWithoutRegionalization);
    this.router.navigateByUrl(newRoute);
  }

  private setShellLanguages(): void {
    this.ngShellService
      .toObservable(watchEnabledRegions)
      .pipe(untilDestroyed(this))
      .subscribe((regions: RegionDetailModel[]) => {
        const languageList = regions
          .flatMap(region => region.languages)
          .map(
            l =>
              `${l.language.substring(0, 2)}${l.language.substring(2, 5).toUpperCase()}`,
          )
          .filter((value, index, self) => self.indexOf(value) === index);

        //Add default language from transloco to the list of possible languages even it its not available as the shell langauge
        //Safety fallback for when there is no translation file for created region
        const defaultTranslocoLang = this.translocoService.getDefaultLang();
        languageList.push(defaultTranslocoLang);

        this.translocoService.setAvailableLangs(languageList);
      });
  }
  
  getAccountDetails(accountId){
    this.customerservice.accountSelectorData(accountId);
  }

  async makeAccountCall(): Promise<void> {
    this.customerservice.getAccountDropdownList().subscribe(
      res => {
          res.body?.response.content.accounts.some(obj => 'addresses' in obj)
          ?this.selectOptList.push(...res.body?.response.content.accounts.map( acc => ({label: acc.account_name, value: acc.crmNumber, title: `${acc.crmNumber} (${acc.erpNumber})`, primary_address: acc.addresses[0].street, secondary_address: `${acc.addresses[0].city}, ${acc.addresses[0].state}, ${acc.addresses[0].country}`}) ))
          :this.selectOptList.push(...res.body?.response.content.accounts.map( acc => ({label: acc.account_name, value: acc.crmNumber, title: `${acc.crmNumber} (${acc.erpNumber})`, primary_address: acc.account_name})))
      }
    );
  }
  
  getLocalId(){
    this.customerservice.localId$.subscribe(localid => {
      this.localId = localid;
      if(this.localId){
        this.localIdBool = true;
      }else{
        this.localIdBool = false;
      }
      this.changeDetect.detectChanges();
    });
  }

  navigationToggle(){
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(
      (event: NavigationEnd) => {
        if (window.location.href.includes('main')) {
          this.localIdBool = false;
          this.changeDetect.detectChanges();
        }
        if (window.location.href.includes('customer-notification-details')) {
          this.localIdBool = true;
          this.changeDetect.detectChanges();
        }
      }
    );
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
